import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaUserFriends, FaBolt, FaMagic, FaCompass } from 'react-icons/fa';
import { postsAPI, bookmarksAPI, securityAPI, usersAPI, promotedAPI } from '../utils/api';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import CreatePostModal from '../components/CreatePostModal';
import StoriesBar from '../components/StoriesBar';
import LiveStreamsBar from '../components/LiveStreamsBar';
import PostCard from '../components/feed/PostCard';
import QuotePostModal from '../components/feed/QuotePostModal';
import EditPostModal from '../components/feed/EditPostModal';
import { reactions } from '../components/feed/feedUtils';

const FEED_TABS = [
  { key: 'foryou', label: 'For You', icon: FaMagic, gradient: 'from-[#4A90E2] to-[#A855F7]' },
  { key: 'following', label: 'Following', icon: FaUserFriends, gradient: 'from-[#4A90E2] to-[#A855F7]' },
  { key: 'trending', label: 'Trending', icon: FaBolt, gradient: 'from-orange-500 to-red-500' },
  { key: 'discover', label: 'Discover', icon: FaCompass, gradient: 'from-[#4A90E2] to-[#A855F7]' },
];

const Feed = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreatePost, setShowCreatePost] = useState(false);
  const [expandedComments, setExpandedComments] = useState({});
  const [comments, setComments] = useState({});
  const [newComment, setNewComment] = useState({});
  const [activeTab, setActiveTab] = useState('following');
  const [showQuoteModal, setShowQuoteModal] = useState(null);
  const [quoteContent, setQuoteContent] = useState('');
  const [bookmarkedPosts, setBookmarkedPosts] = useState(new Set());
  const [likedPosts, setLikedPosts] = useState(new Set());
  const [followedUsers, setFollowedUsers] = useState(new Set());
  const [editingPost, setEditingPost] = useState(null);
  const [editContent, setEditContent] = useState('');
  const navigate = useNavigate();
  const currentUser = JSON.parse(sessionStorage.getItem('user') || '{}');

  useEffect(() => { loadFeed(); }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (currentUser.id) {
      usersAPI.getFollowing(currentUser.id).then(res => {
        setFollowedUsers(new Set((res.data || []).map(u => u.id)));
      }).catch(() => {});
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadFeed = async () => {
    setLoading(true);
    try {
      const fetchers = { foryou: postsAPI.getForYouFeed, discover: postsAPI.getDiscoverFeed, trending: postsAPI.getTrendingFeed, following: postsAPI.getFollowingFeed };
      const [response, promoRes] = await Promise.all([
        (fetchers[activeTab] || postsAPI.getFollowingFeed)(),
        promotedAPI.getFeed(2).catch(() => ({ data: [] })),
      ]);
      // Inject promoted posts naturally into feed (every 5th position)
      const feedPosts = [...response.data];
      const promos = promoRes.data || [];
      promos.forEach((promo, i) => {
        const pos = Math.min(4 + i * 5, feedPosts.length);
        feedPosts.splice(pos, 0, { ...promo, id: promo.id, _isAd: true });
      });
      setPosts(feedPosts);
    } catch (error) {
      console.error('Failed to load feed', error);
      if (error.response?.status === 401) {
        if (activeTab !== 'following') {
          try { const fb = await postsAPI.getFeed(); setPosts(fb.data); return; } catch (e) { console.error('Feed fallback failed', e); }
        }
        navigate('/');
      }
    } finally { setLoading(false); }
  };

  const handleReaction = async (postId, reactionType) => {
    try {
      await postsAPI.react(postId, reactionType);
      setPosts(prev => prev.map(p => p.id === postId ? { ...p, likes_count: p.likes_count + 1 } : p));
      setLikedPosts(prev => new Set([...prev, postId]));
      toast.success(`Reacted with ${reactions.find(r => r.type === reactionType)?.icon}`);
    } catch { toast.error('Failed to react'); }
  };

  const handleLike = async (postId) => {
    try {
      await postsAPI.like(postId);
      const isLiked = likedPosts.has(postId);
      setPosts(prev => prev.map(p => p.id === postId ? { ...p, likes_count: isLiked ? p.likes_count - 1 : p.likes_count + 1 } : p));
      setLikedPosts(prev => { const s = new Set(prev); isLiked ? s.delete(postId) : s.add(postId); return s; });
    } catch { toast.error('Failed to like post'); }
  };

  const handleRepost = async (postId) => {
    try {
      const res = await postsAPI.repost(postId);
      setPosts(prev => prev.map(p => p.id === postId ? { ...p, shares_count: res.data.reposted ? p.shares_count + 1 : p.shares_count - 1 } : p));
      toast.success(res.data.reposted ? 'Reposted!' : 'Repost removed');
    } catch { toast.error('Failed to repost'); }
  };

  const handleBookmark = async (postId) => {
    try {
      await bookmarksAPI.toggle(postId);
      const was = bookmarkedPosts.has(postId);
      setBookmarkedPosts(prev => { const s = new Set(prev); was ? s.delete(postId) : s.add(postId); return s; });
      toast.success(was ? 'Removed from bookmarks' : 'Added to bookmarks');
    } catch { toast.error('Failed to bookmark'); }
  };

  const handleQuotePost = async (postId) => {
    if (!quoteContent.trim()) { toast.error('Please add your thoughts'); return; }
    try {
      await postsAPI.quote(postId, quoteContent);
      toast.success('Quote posted!');
      setShowQuoteModal(null);
      setQuoteContent('');
      loadFeed();
    } catch { toast.error('Failed to quote post'); }
  };

  const toggleComments = async (postId) => {
    if (expandedComments[postId]) {
      setExpandedComments(prev => ({ ...prev, [postId]: false }));
    } else {
      try {
        const res = await postsAPI.getComments(postId);
        setComments(prev => ({ ...prev, [postId]: res.data }));
        setExpandedComments(prev => ({ ...prev, [postId]: true }));
      } catch { toast.error('Failed to load comments'); }
    }
  };

  const handleAddComment = async (postId) => {
    const content = newComment[postId];
    if (!content?.trim()) return;
    try {
      const res = await postsAPI.addComment(postId, content);
      setComments(prev => ({ ...prev, [postId]: [...(prev[postId] || []), res.data] }));
      setPosts(prev => prev.map(p => p.id === postId ? { ...p, comments_count: p.comments_count + 1 } : p));
      setNewComment(prev => ({ ...prev, [postId]: '' }));
      toast.success('Comment added!');
    } catch { toast.error('Failed to add comment'); }
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;
    try { await postsAPI.delete(postId); setPosts(prev => prev.filter(p => p.id !== postId)); toast.success('Post deleted'); }
    catch { toast.error('Failed to delete post'); }
  };

  const handleReportPost = async (targetId) => {
    const reason = window.prompt('Reason: spam, harassment, hate, violence, misinformation, or other');
    if (!reason) return;
    try { await securityAPI.report({ target_type: 'post', target_id: targetId, reason: reason.toLowerCase() }); toast.success('Report submitted'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed to report'); }
  };

  const handleBlockUser = async (userId) => {
    if (!window.confirm('Block this user?')) return;
    try { await securityAPI.blockUser(userId); setPosts(prev => prev.filter(p => p.user_id !== userId)); toast.success('User blocked'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed to block'); }
  };

  const handleFollow = async (userId) => {
    try {
      if (followedUsers.has(userId)) { await usersAPI.unfollow(userId); setFollowedUsers(prev => { const s = new Set(prev); s.delete(userId); return s; }); toast.success('Unfollowed'); }
      else { await usersAPI.follow(userId); setFollowedUsers(prev => new Set(prev).add(userId)); toast.success('Following!'); }
    } catch { toast.error('Failed to update follow'); }
  };

  const handleEditPost = (post) => { setEditingPost(post); setEditContent(post.content || ''); };

  const handleSaveEdit = async () => {
    if (!editingPost) return;
    try { await postsAPI.edit(editingPost.id, { content: editContent }); setPosts(prev => prev.map(p => p.id === editingPost.id ? { ...p, content: editContent, is_edited: true } : p)); setEditingPost(null); toast.success('Post updated'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed to update'); }
  };

  const emptyStates = {
    following: { icon: FaUserFriends, title: 'Your Following feed is empty', sub: 'Follow people to see their posts here', btn: 'Discover People', action: () => setActiveTab('discover') },
    trending: { icon: FaBolt, title: 'Nothing trending right now', sub: 'Be the first to start a trend!', btn: <span>Create a <span className="font-arthaus">Point</span></span>, action: () => setShowCreatePost(true) },
    foryou: { icon: FaMagic, title: 'Your personalized feed is building', sub: 'Like and interact with posts to train your feed', btn: 'Explore Discover', action: () => setActiveTab('discover') },
    discover: { icon: FaCompass, title: 'No points yet. Be the first to share!', sub: '', btn: <span>Create Your First <span className="font-arthaus">Point</span></span>, action: () => setShowCreatePost(true) },
  };

  return (
    <>
      <div className="min-h-screen bg-white">
        <div className="grid grid-cols-1 md:grid-cols-12">
          <Sidebar />
          <main className="col-span-12 md:col-span-10" data-testid="feed-page">
            <LiveStreamsBar />
            <StoriesBar />
            <div className="p-6 md:p-10">
              <div className="max-w-2xl mx-auto">
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                  {/* Tabs + Create Button */}
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-8">
                    <div className="flex space-x-1 bg-gray-100 p-1 rounded-full overflow-x-auto">
                      {FEED_TABS.map(t => (
                        <button key={t.key} onClick={() => setActiveTab(t.key)} data-testid={`${t.key}-tab`} className={`flex items-center space-x-1.5 px-3 sm:px-4 py-2.5 rounded-full font-medium transition-all text-xs sm:text-sm whitespace-nowrap ${activeTab === t.key ? `bg-gradient-to-r ${t.gradient} text-white shadow-md` : 'text-gray-500 hover:text-gray-700'}`}>
                          <t.icon size={13} /><span>{t.label}</span>
                        </button>
                      ))}
                    </div>
                    <button data-testid="feed-create-post-btn" onClick={() => setShowCreatePost(true)} className="bg-gradient-to-r from-[#4A90E2] to-[#A855F7] text-white hover:opacity-90 font-bold rounded-full px-6 py-3 transition-all hover:scale-105 active:scale-95">
                      <span className="font-arthaus">Create Point</span>
                    </button>
                  </div>

                  {/* Content */}
                  {loading ? (
                    <div className="space-y-4">{[1, 2, 3].map(i => <div key={`skeleton-${i}`} className="bg-gray-100 rounded-2xl h-48 animate-pulse" />)}</div>
                  ) : posts.length === 0 ? (
                    <div className="bg-gray-50 border border-gray-200 rounded-2xl p-12 text-center">
                      {(() => { const es = emptyStates[activeTab] || emptyStates.discover; const Icon = es.icon; return (<><Icon className="text-4xl text-gray-300 mx-auto mb-4" /><p className="text-gray-600 font-medium mb-2">{es.title}</p>{es.sub && <p className="text-gray-400 text-sm mb-4">{es.sub}</p>}<button onClick={es.action} className={`bg-gradient-to-r ${activeTab === 'trending' ? 'from-orange-500 to-red-500' : 'from-[#4A90E2] to-[#A855F7]'} text-white hover:opacity-90 font-bold rounded-full px-6 py-3 transition-transform hover:scale-105 active:scale-95`}>{es.btn}</button></>); })()}
                    </div>
                  ) : (
                    <div className="space-y-6" data-testid="posts-container">
                      {posts.map(post => (
                        <PostCard
                          key={post.id}
                          post={post}
                          currentUser={currentUser}
                          likedPosts={likedPosts}
                          bookmarkedPosts={bookmarkedPosts}
                          followedUsers={followedUsers}
                          expandedComments={expandedComments}
                          comments={comments}
                          newComment={newComment}
                          onLike={handleLike}
                          onReaction={handleReaction}
                          onRepost={handleRepost}
                          onBookmark={handleBookmark}
                          onQuote={setShowQuoteModal}
                          onComment={handleAddComment}
                          onDelete={handleDeletePost}
                          onReport={handleReportPost}
                          onBlock={handleBlockUser}
                          onFollow={handleFollow}
                          onEdit={handleEditPost}
                          onNewCommentChange={(id, val) => setNewComment(prev => ({ ...prev, [id]: val }))}
                          onToggleComments={toggleComments}
                        />
                      ))}
                    </div>
                  )}
                </motion.div>
              </div>
            </div>
          </main>
        </div>

        {showCreatePost && <CreatePostModal onClose={() => setShowCreatePost(false)} onPostCreated={() => { loadFeed(); toast.success('Post created successfully!'); }} />}
        <QuotePostModal post={showQuoteModal} quoteContent={quoteContent} setQuoteContent={setQuoteContent} onSubmit={handleQuotePost} onClose={() => setShowQuoteModal(null)} />
        <EditPostModal post={editingPost} editContent={editContent} setEditContent={setEditContent} onSave={handleSaveEdit} onClose={() => setEditingPost(null)} />
      </div>
    </>
  );
};

export default Feed;
